import { Controller } from "@hotwired/stimulus";

// Inline edit cell for list views — click on a styled value (e.g. a statut
// pill), reveals a Select2-driven dropdown anchored to the cell, picks a
// value, fires a PATCH against the resource's "move" endpoint with
// `group_field` + `value`, and swaps the displayed text on success.
//
// Markup contract :
//   <td data-controller="inline-field"
//       data-inline-field-url-value="/juniors/1/prospections/42/move"
//       data-inline-field-field-value="statut"
//       data-inline-field-options-value='[{"v":"En attente","label":"…","color":"…"}]'
//       data-inline-field-current-value="En attente">
//     <span data-inline-field-target="display"
//           data-action="click->inline-field#open"
//           class="statut-pill ...">En attente</span>
//   </td>
//
// The display element handles its own "blank" rendering (when current is
// empty, the controller still allows opening — useful to assign a value).
export default class extends Controller {
  static targets = ["display"];
  static values  = {
    url:     String,
    field:   String,
    options: Array,
    current: String,
  };

  open(event) {
    event.preventDefault();
    if (this._select) return; // already open
    const $ = window.jQuery || window.$;

    // Build a minimal <select> in place of the display, fronted by Select2.
    const wrap = document.createElement("div");
    wrap.className = "inline-field__wrap";
    const select = document.createElement("select");
    select.className = "inline-field__select";
    const blank = document.createElement("option");
    blank.value = ""; blank.textContent = "—";
    select.appendChild(blank);
    (this.optionsValue || []).forEach((opt) => {
      const o = document.createElement("option");
      o.value = opt.v; o.textContent = opt.label || opt.v;
      if (this.currentValue === opt.v) o.selected = true;
      select.appendChild(o);
    });
    wrap.appendChild(select);

    this.displayTarget.hidden = true;
    this.element.appendChild(wrap);

    if ($ && $.fn.select2) {
      $(select).select2({
        placeholder: "—",
        allowClear: false,
        width: "180px",
        dropdownParent: $(this.element),
        minimumResultsForSearch: 4,
      });
      $(select).select2("open");
      $(select).on("select2:close", () => this._cleanup());
      $(select).on("change", (e) => this._submit(e.target.value));
    } else {
      // Fallback : native select if jQuery/Select2 isn't available.
      select.focus();
      select.addEventListener("change", () => this._submit(select.value));
      select.addEventListener("blur", () => this._cleanup());
    }

    this._select = select;
  }

  async _submit(newValue) {
    if (newValue === this.currentValue) return;
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
    const body = new URLSearchParams();
    body.append("group_field", this.fieldValue);
    body.append("value", newValue || "");

    try {
      const res = await fetch(this.urlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": csrf, "Accept": "application/json" },
        body,
      });
      if (!res.ok) throw new Error("HTTP " + res.status);
      this.currentValue = newValue;
      this._refreshDisplay(newValue);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert("Échec de la mise à jour : " + (e.message || "erreur réseau"));
    } finally {
      this._cleanup();
    }
  }

  _refreshDisplay(newValue) {
    const opt = (this.optionsValue || []).find((o) => o.v === newValue);
    const label = opt ? (opt.label || opt.v) : "—";
    const color = opt?.color;
    // Reset current pill class. We strip BOTH `statut-pill--*` tones AND the
    // `is-muted` empty-state. Then re-add `statut-pill` + the new tone if
    // there's a value — without `statut-pill` base class the pill background
    // / padding don't render (the bug when the cell was empty originally).
    const base = Array.from(this.displayTarget.classList)
      .filter((c) => !c.startsWith("statut-pill") && c !== "is-muted");
    this.displayTarget.className = base.join(" ");
    if (newValue) {
      this.displayTarget.classList.add("statut-pill");
      this.displayTarget.classList.add(this._toneFor(newValue));
    } else {
      this.displayTarget.classList.add("is-muted");
    }
    if (color) this.displayTarget.style.setProperty("--statut-bg", color);
    this.displayTarget.textContent = newValue ? label : "—";
  }

  _toneFor(value) {
    // Heuristic — leans on the existing tone classes used everywhere.
    if (["Converti en client", "RDV pris"].includes(value))            return "statut-pill--signe";
    if (["Non converti", "Pas intéressé", "Sans suite"].includes(value)) return "statut-pill--avorte";
    if (["En attente de contact", "À relancer"].includes(value))       return "statut-pill--standby";
    return "statut-pill--negociation";
  }

  _cleanup() {
    const $ = window.jQuery || window.$;
    if (this._select) {
      try {
        if ($ && $.fn.select2 && $(this._select).hasClass("select2-hidden-accessible")) {
          $(this._select).select2("destroy");
        }
      } catch (_e) { /* noop */ }
      this._select.parentElement?.remove();
      this._select = null;
    }
    if (this.hasDisplayTarget) this.displayTarget.hidden = false;
  }
}
